// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  Grid,
  Card,
  Box,
  Button,
  Tooltip,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/Icons/lucideIcons/trash-2.svg';
import { useContextMenu } from 'react-contexify';
import ImpactAnalysisModal from '../../../Common/ModelInventoryAndAssociation/Modals/ImpactAnalysisModal.jsx';
import ReactMuiTableListView from '../../ReactMuiTableListViewV8';
import { ContextMenus } from '../../ContextMenus';
import {
  formatAttributeValue,
  getTemplateAttributes,
  isValueEmpty,
} from '../../../../utils/Utils';
import SwalToast from '../../swalToast';
import {
  getVisibleAttributes,
  shouldShowAttribute,
} from '../../../../utils/conditionalRuleEvaluator';
import { extractAllAttributesFromTemplate } from '../../../../features/configuration/template/components/SectionAndAttribute/commonFunction';
import {
  HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES,
  validateDeploymentDate,
  validateDeploymentDateCommunication,
} from '../../../../utils/deploymentDateUtils';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import { areSetsEqual } from '../BasicDetails/BasicDetails';
import {
  applyDateCalendarConstraints,
  applyMandatoryConstraints,
  createInitialFormValues,
} from '../../../../utils/formUtils';
import useEntityDetailsForm from '../../../../hooks/useEntityDetailsForm';
import ValueCellRenderer from '../OtherDetails/ValueCellRenderer';
import NameCellRenderer from '../OtherDetails/NameCellRenderer';
import SectionAttributePopover from '../../SectionAttributePopover';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';
import useEntity from '../../../../features/configuration/entityManagement/hooks/useEntity';
import useModelCompletedCycle from '../../../../hooks/ModelInventory/useModelCompletedCycle';
import useTextOverflow from '../../../../hooks/useTextOverFlow';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import useSectionPermissions from '../../../../hooks/ModelInventory/useSectionPermissions';
import useCommon from '../../../../hooks/useCommon';
import {
  useSingleWorkflowQuery,
  useWorkflowGroupsQuery,
} from '../../../../features/configuration/workflow/api/workflowQueries';

const handleGetHeaderProps = () => ({
  style: {
    display: 'flex',
    alignItems: 'center',
  },
});

const CELL_STYLE_CONFIG = {
  minHeight: '40px',
  formControl: {
    marginTop: '0px',
    label: {
      display: 'none',
    },
  },
};

/**
 * AttributeValueRenderer component for rendering individual attribute values
 * @param {Object} props - Component props
 * @param {Object} props.attr - The attribute data
 * @param {Function} props.renderValueCell - Function to render value cell
 * @returns {React.ReactElement} The rendered attribute value
 */
const AttributeValueRenderer = ({ attr, renderValueCell }) => {
  return (
    <>
      {renderValueCell({
        row: { original: attr },
        value: attr.value,
      })}
    </>
  );
};

AttributeValueRenderer.propTypes = {
  attr: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ]),
  }).isRequired,
  renderValueCell: PropTypes.func.isRequired,
};

const excludedDataTypes = [
  'Chart',
  'Table',
  'AdvancedTable',
  'Association',
  'Document',
  'IssueType',
];

/**
 * JustificationInput component for rendering a justification text field inside
 * the change-request table. Defined at module level (not inside a useCallback)
 * to comply with the Rules of Hooks — hooks may only be called from React
 * function components or custom hooks, not from plain callbacks.
 *
 * @param {Object} props
 * @param {string|number} props.attributeId - ID of the attribute row
 * @param {string} props.fieldName - Formik field name (string cast of attributeId)
 * @param {string} props.justificationValue - Current justification text
 * @param {Object} props.context - Shared cell context (justificationFormikForm, getJustificationErrorText, mode)
 */
const JustificationInput = ({
  attributeId,
  fieldName,
  justificationValue,
  context,
}) => {
  const handleJustificationChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      context.justificationFormikForm.setFieldValue(fieldName, newValue);
    },
    [context.justificationFormikForm, fieldName]
  );

  const handleJustificationBlur = useCallback(() => {
    context.justificationFormikForm.setFieldTouched(fieldName, true);
  }, [context.justificationFormikForm, fieldName]);

  return (
    <TextField
      size="small"
      name={fieldName}
      value={justificationValue}
      onChange={handleJustificationChange}
      onBlur={handleJustificationBlur}
      placeholder="Enter justification"
      error={Boolean(
        context.justificationFormikForm.touched[attributeId] &&
        context.justificationFormikForm.errors[attributeId]
      )}
      helperText={context.getJustificationErrorText(
        context.justificationFormikForm.errors[attributeId],
        context.justificationFormikForm.touched[attributeId]
      )}
      fullWidth
      sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: '40px',
        },
      }}
      slotProps={{
        htmlInput: { maxLength: 200 },
      }}
    />
  );
};

JustificationInput.propTypes = {
  attributeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  fieldName: PropTypes.string.isRequired,
  justificationValue: PropTypes.string,
  context: PropTypes.shape({
    justificationFormikForm: PropTypes.shape({
      setFieldValue: PropTypes.func.isRequired,
      setFieldTouched: PropTypes.func.isRequired,
      values: PropTypes.objectOf(PropTypes.string).isRequired,
      touched: PropTypes.objectOf(PropTypes.bool).isRequired,
      errors: PropTypes.objectOf(PropTypes.string).isRequired,
    }).isRequired,
    getJustificationErrorText: PropTypes.func.isRequired,
    mode: PropTypes.string,
  }).isRequired,
};

JustificationInput.defaultProps = {
  justificationValue: '',
};

const permissionRank = {
  none: 0,
  view: 1,
  manage: 2,
};

// Below are the props which we will get the values from props previously now we have to get this from APIs or plugins, etc
// sectionsData = all sections of the selected template with attributes
const isTableView = true;
const isHDFCUAMEnabled = true; // get this from plugins
const handleAutoSave = () => {};
const syncAttributes = {};
const formDetails = {}; // template data
const allAttributeChats = []; // this is for chat count on attributes
// const sectionPermissions = {}; // this is for section permissions
const currentModelStatus = {}; // this is for getting current model status for validating deployment date in HDFCUAM
const isUserAuthorizedToOverrideAttribute = true; // this is for checking user permission to show override option for derived attribute
const isUserAuthorizedForUpdateAttribute = true; // this is for checking user permission to edit attribute
const overrideData = {}; // this is for storing override data for derived attribute

const hasDraftChanges = false; // this is for checking whether there are any unsaved changes or not
const handleOpenChat = () => {}; // this is for handling chat open on attribute
const handleAttributeClick = () => {}; // this is for handling attribute click to show details or something else on click of attribute
const groupDetails = {}; // this is for group details in case of group select attribute

/**
 * Recursively extracts all attribute IDs from a conditional rule formula structure.
 *
 * @param {Object} formula - The conditional rule formula object to traverse.
 * @returns {Array<string|number>} An array of dependent attribute IDs found in the formula rules.
 */
const getDependentAttributeIds = (formula) => {
  if (!formula) return [];
  const ids = [];
  const traverse = (node) => {
    if (!node) return;
    if (node.attribute_id !== undefined && node.attribute_id !== null) {
      ids.push(node.attribute_id);
    }
    if (node.rules && Array.isArray(node.rules)) {
      node.rules.forEach(traverse);
    }
    if (node.rule_groups && Array.isArray(node.rule_groups)) {
      node.rule_groups.forEach(traverse);
    }
  };
  traverse(formula);
  return ids;
};

const FieldChangeOtherDetails = ({
  attribute,
  entityDetailsFormikForm: currentEntityForm,
  value,
  usersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
  teamUsersList = EMPTY_ARRAY,
  mode = 'edit',
  onChange,
  showLabel = true,
  label,
  modelTemplateDetails = EMPTY_OBJECT,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const prevHiddenIdsRef = React.useRef(new Set());
  const [attributeSourceMapping, setAttributeSourceMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(true);
  const [attributeValues, setAttributeValues] = useState({});

  // Constraint-related state
  const [lookupSourceAttributes, setLookupSourceAttributes] = useState([]);
  const [autoCompleteSourceAttributes, setAutoCompleteSourceAttributes] =
    useState([]);
  const [overrideDropdownOptions, setOverrideDropdownOptions] = useState({});
  const [dateCalendarConstraints, setDateCalendarConstraints] = useState({});
  const [dateCalendarSourceAttributes, setDateCalendarSourceAttributes] =
    useState([]);
  const [mandatoryConstraints, setMandatoryConstraints] = useState({});
  const [mandatorySourceAttributes, setMandatorySourceAttributes] = useState(
    []
  );
  const [isLoadingConstraints, setIsLoadingConstraints] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [constraintCallChain, setConstraintCallChain] = useState(new Set());
  const [attributeDetails, setAttributeDetails] = useState({});
  const [prevAttributeDetails, setPrevAttributeDetails] = useState({});
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [attributeToBeAdded, setAttributeToBeAdded] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [templateStructure, setTemplateStructure] = useState(null);
  const [userAddedTemplateStructure, setUserAddedTemplateStructure] =
    useState(null);
  const [previousAttributeValue, setPreviousAttributeValue] = useState({});

  const [openActionModal, setOpenActionModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactedData, setImpactedData] = useState(EMPTY_OBJECT);
  const [isImpactModalOpen, setIsImpactModalOpen] = useState(false);
  const [impactEntityType, setImpactEntityType] = useState('');
  // Ref to track whether component get Opened first time or not to avoid unnecessary API calls on component open and close
  const isFirstOpenRef = useRef(false);
  // Ref to track constraint call chain to avoid stale closures
  const constraintCallChainRef = useRef(new Set());
  // Ref to track auto-populate timers for cleanup
  const autoPopulateTimersRef = useRef([]);
  // ref to track edit button disabled prev value

  const { dispatch, setCurrentWorkflowGroupId } = useCommon();
  const { getModelCompletedCycle } = useModelCompletedCycle();
  const { handleSetSectionPermission } = useSectionPermissions();
  const { show: showRowLevelMenus } = useContextMenu();
  const theme = useTheme();
  const { execute: getWorkflowGroups } = useWorkflowGroupsQuery({
    options: {
      enabled: false, // don't fetch on mount, we'll call when needed
    },
  });
  const { execute: getSingleWorkflow } = useSingleWorkflowQuery({
    options: {
      enabled: false, // don't fetch on mount, we'll call when needed
    },
  });

  // Helper function to get error helper text
  const getJustificationErrorText = useCallback(
    (error, touched) => (touched && error ? error : ''),
    []
  );

  const {
    getAttributeDetails,
    getSingleEntityDetails,
    getImpactedAttributeData,
  } = useModelInventory();
  const { getCustomEntityTemplate } = useEntity();

  // get linked attribute value from formik form
  const entityDetails = useMemo(() => {
    const linkedAttributeId = attribute?.linked_attribute;
    if (linkedAttributeId) {
      return currentEntityForm?.values?.[linkedAttributeId];
    }
    return null;
  }, [currentEntityForm?.values, attribute?.linked_attribute]);

  // Entity data for FieldRender
  const entityData = useMemo(
    () => ({
      entity_id: entityDetails?.entity_id,
      entity_type: entityDetails?.entity_type,
    }),
    [entityDetails]
  );
  const allHiddenAttributes = useMemo(() => {
    return getVisibleAttributes(attributeOptions, {})?.hiddenAttributes;
  }, [attributeOptions]);

  // Add hidden attributes to userAddedTemplateStructure when they are available
  useEffect(() => {
    if (!allHiddenAttributes?.length || !userAddedTemplateStructure) {
      return;
    }

    setUserAddedTemplateStructure((prevStructure) => {
      const updatedStructure = JSON.parse(JSON.stringify(prevStructure));

      allHiddenAttributes.forEach((hiddenAttr) => {
        const attributeId = hiddenAttr.id;
        let attributeData = null;
        let belongsToSection = null;

        // Find attribute in original template sections
        templateStructure.sections?.forEach((section) => {
          const foundAttr = section.attributes?.find(
            (attr) => attr.id === attributeId
          );
          if (foundAttr) {
            attributeData = foundAttr;
            belongsToSection = section.section_id;
          }
        });

        // If not found in sections, check basic attributes
        if (!attributeData) {
          attributeData = templateStructure.attributes?.find(
            (attr) => attr.id === attributeId
          );
        }

        if (!attributeData) {
          return;
        }

        if (belongsToSection) {
          // Find or create the section
          let targetSection = updatedStructure.sections.find(
            (s) => s.section_id === belongsToSection
          );

          if (!targetSection) {
            // Get original section details
            const originalSection = templateStructure.sections.find(
              (s) => s.section_id === belongsToSection
            );

            targetSection = { ...originalSection, attributes: [] };
            updatedStructure.sections.push(targetSection);
          }

          // Add attribute if not already present
          const attrExists = targetSection.attributes.some(
            (attr) => attr.id === attributeId
          );
          if (!attrExists) {
            targetSection.attributes.push(attributeData);
          }
        } else {
          // Add to basic attributes if not already present
          const attrExists = updatedStructure.attributes.some(
            (attr) => attr.id === attributeId
          );
          if (!attrExists) {
            updatedStructure.attributes.push(attributeData);
          }
        }
      });
      return updatedStructure;
    });
    // Remove added attributes from options as they inside userAddedTemplateStructure now
    // we are just not visible in table because they are hidden

    // TODO: enhance this in next related task
    // const addedIds = allHiddenAttributes.map((attr) => attr.id);
    // setAttributeOptions((prev) =>
    //   prev.filter((attr) => !addedIds.includes(attr.id))
    // );
  }, [allHiddenAttributes, templateStructure]);

  const filterSectionsByPrevNodeHistory = useCallback(
    (
      templateData,
      visitedModelNodes,
      selectedModelEntity,
      workflowDetails,
      workflowGroups
    ) => {
      if (
        isValueEmpty(templateData) ||
        isValueEmpty(visitedModelNodes) ||
        isValueEmpty(templateData?.sections)
      ) {
        return EMPTY_OBJECT;
      }

      // list of workflow group ids for visited nodes
      const visitedWorkflowGroupIds = new Set();
      workflowDetails?.flow_display?.node?.forEach((node) => {
        // if node is present in visitedModelNodes then add its workflow group to visitedWorkflowGroups set
        if (visitedModelNodes?.includes(node?.id) && node?.parentId) {
          const matchedGroups = workflowGroups?.find(
            (group) => group?.group_name === node?.parentId
          );

          visitedWorkflowGroupIds.add(matchedGroups?.workflow_group_id);
        }
      });

      const sectionPermissions = templateData?.sections?.reduce(
        (acc, section) => {
          const permissions = section?.permission || EMPTY_OBJECT;

          /**
           * we will iterate on all nodes and find the highest permission for the section
           * if user has view permission in one node and manage permission in another node
           * then we will consider manage permission as the highest permission
           */
          const highestPermission = visitedModelNodes.reduce(
            (highest, node) => {
              const currentPermission = permissions[node] || 'none';

              // compare currentPermission with highest and return the highest permission
              return permissionRank[currentPermission] > permissionRank[highest]
                ? currentPermission
                : highest;
            },
            'none'
          );

          // eslint-disable-next-line no-param-reassign
          acc[section.section_id] = highestPermission;

          return acc;
        },
        {}
      );

      // filtered section which has manage permission only
      const filteredSectionsOnPermission = templateData.sections.filter(
        (section) => {
          const permission = sectionPermissions[section.section_id];
          return permission === 'manage';
        }
      );

      /**
       * if any section have allow_grouping on then section's show_in_workflow_group list should have atleast
       * one group which is in visitedGroups otherwise we will filter out that section as well from the list of sections
       */
      const filteredSectionsByGroups = filteredSectionsOnPermission?.filter(
        (section) => {
          /**
           * if allow_grouping is on for the section then we will check
           * else we will show that section
           */
          if (section?.allow_grouping) {
            const allowedGroups =
              section?.show_in_workflow_group || EMPTY_ARRAY;

            // check if allowedGroups have any group which is in visitedWorkflowGroupIds
            const hasGroupIntersection = allowedGroups.some((groupId) =>
              visitedWorkflowGroupIds.has(groupId)
            );
            /**
             * as section have allow_grouping on and user have visited the group in workflow history so we can show that section to user
             */
            if (hasGroupIntersection) {
              return true;
            }

            /**
             * if not intersection found then return false as section have allow_grouping on
             * but user don't have visited the group in workflow history so we can't show that section to user
             */
            return false;
          }
          return true;
        }
      );

      // set these permission in redux to use in other places as well where we are checking section permissions
      handleSetSectionPermission({
        data: {
          section_permissions: sectionPermissions,
          conditional_sections: {},
        },
        entity_id: selectedModelEntity?.entity_id,
      });

      /**
       * also set current workflow group id as null, coz we are calculating permissions on based of multiple prev nodes
       * history and there can be different workflow groups for different nodes
       */
      dispatch(
        setCurrentWorkflowGroupId({
          entity_id: selectedModelEntity?.entity_id,
          data: null,
        })
      );

      return {
        ...templateData,
        sections: filteredSectionsByGroups,
      };
    },
    [handleSetSectionPermission, setCurrentWorkflowGroupId, dispatch]
  );

  // func to fetch sections and attributes of linked entity
  const handleGetTemplateDetails = useCallback(async () => {
    const entityRes = await getSingleEntityDetails({
      entityType: entityDetails?.entity_type || 'ModelInventory',
      entityId: entityDetails?.entity_id,
    });

    if (!entityRes?.data?.modelEntity) {
      return;
    }

    const modelEntity = entityRes.data.modelEntity || entityRes.data;

    setImpactEntityType(modelEntity?.entity_type_id);
    const templateId = modelEntity?.template_id;
    const versionId =
      modelEntity?.template_version_id || modelEntity?.version_id;

    const templateDataPromise = getCustomEntityTemplate({
      template_id: templateId,
      version_id: versionId,
      for_change_request: true,
    });

    const modelNodeHistoryPromise = getModelCompletedCycle({
      entityType: modelEntity?.entity_type,
      entityId: modelEntity?.entity_id,
      globalSearch: false,
      text: '',
      requestType: modelEntity?.request_type,
      logActions: ['lifecycle_transition'],
      attributes: [],
      users: [],
      dateRange: null,
    });

    const workflowDetailsPromise = getSingleWorkflow({
      params: {
        workflowType: modelEntity?.workflow,
      },
    });

    const workflowGroupsPromise = getWorkflowGroups({
      params: {
        template_id: templateId,
        version_id: versionId,
      },
    });

    const [templateData, modelNodeHistory, workflowDetails, workflowGroups] =
      await Promise.all([
        templateDataPromise,
        modelNodeHistoryPromise,
        workflowDetailsPromise,
        workflowGroupsPromise,
      ]).then(
        ([
          templateDataRes,
          modelNodeHistoryRes,
          workflowDetailsRes,
          workflowGroupsRes,
        ]) => [
          templateDataRes?.data,
          modelNodeHistoryRes, // we will get history array in data itself from this function API instead of res.data, coz we are modying the API response inside firing function
          workflowDetailsRes,
          workflowGroupsRes,
        ]
      );

    setTemplateStructure(templateData);

    setUserAddedTemplateStructure({
      ...templateData,
      sections: [],
      attributes: [],
    });

    let visitedModelNodes = modelNodeHistory || EMPTY_ARRAY;
    if (isValueEmpty(modelNodeHistory)) {
      // when no history present for the model then set current status as the only entry in lifecycle history
      visitedModelNodes = modelEntity?.status
        ? [modelEntity.status]
        : EMPTY_ARRAY;
    }

    const filteredTemplateData = filterSectionsByPrevNodeHistory(
      templateData,
      visitedModelNodes,
      modelEntity,
      workflowDetails,
      workflowGroups
    );

    const preparedAttributes = getTemplateAttributes(
      filteredTemplateData,
      excludedDataTypes,
      true
    );

    setAttributeOptions(preparedAttributes);
  }, [
    entityDetails,
    modelTemplateDetails?.is_change_request,
    setAttributeOptions,
    setUserAddedTemplateStructure,
    setTemplateStructure,
    getCustomEntityTemplate,
    getSingleEntityDetails,
    getModelCompletedCycle,
    getSingleWorkflow,
    getWorkflowGroups,
    filterSectionsByPrevNodeHistory,
  ]);

  // func to fetch attribute values of linked entity
  const handleGetAttributesData = useCallback(() => {
    if (isValueEmpty(entityDetails?.entity_id)) {
      return;
    }
    setLoading(true);
    getAttributeDetails({
      entity_id: entityDetails?.entity_id,
    })
      .then((res) => {
        if (res?.data) {
          setAttributeDetails(res?.data);
          setPrevAttributeDetails(res?.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    getAttributeDetails,
    setLoading,
    setAttributeDetails,
    setPrevAttributeDetails,
    entityDetails,
  ]);

  const handleFieldChangeToAddAttribute = useCallback(
    (_, newValue) => {
      setAttributeToBeAdded(newValue);
    },
    [setAttributeToBeAdded]
  );

  const entityId = entityData?.entity_id;
  const entityType = entityData?.entity_type;
  const isMultipleEditMode = true;
  const { entityDetailsFormikForm, handleSaveOtherDetailsForm, setFormSchema } =
    useEntityDetailsForm({
      formDetails: userAddedTemplateStructure,
      attributeDetails,
      entityId: entityId,
      entityType: entityType,
      successMessage: 'Model Details updated successfully.',
      syncAttributes,
      getDataWithoutApiCall: true,
      skipUnchangedValueCheck: true,
    });

  // Compute allFormValues for conditional evaluation
  const allFormValues = useMemo(() => {
    const baseValues = { ...attributeValues, ...syncAttributes };
    // Add formik values if available
    if (entityDetailsFormikForm?.values) {
      Object.assign(baseValues, entityDetailsFormikForm.values);
    }
    return baseValues;
  }, [attributeValues, syncAttributes, entityDetailsFormikForm?.values]);

  /**
   * Memoized list of attribute options available to be added to the change request.
   * Filters out conditional attributes whose dependencies have not yet been added,
   * or whose visibility rules do not evaluate to true under the current form state.
   *
   * @type {Array<Object>}
   */
  const filteredAttributeOptions = useMemo(() => {
    const addedAttributeIds = new Set(attributes.map((a) => String(a.id)));

    return attributeOptions.filter((attr) => {
      // If the attribute is not conditional, keep it
      if (!attr.show_conditionally || !attr.conditional_formula) {
        return true;
      }

      // It is conditional, so it is a hidden attribute by default.
      // Get the attributes it is dependent on.
      const dependentIds = getDependentAttributeIds(attr.conditional_formula);

      // If it doesn't declare any dependent attributes, keep it
      if (dependentIds.length === 0) {
        return true;
      }

      // Check if all dependent attributes are added manually
      const allDependentsAdded = dependentIds.every((depId) =>
        addedAttributeIds.has(String(depId))
      );

      if (!allDependentsAdded) {
        return false;
      }

      // Check if its rule matches under the current form values
      const isRuleMatched = shouldShowAttribute(attr, allFormValues);

      return isRuleMatched;
    });
  }, [attributeOptions, attributes, allFormValues]);

  // Transform attributes array to include values from attributeValues and apply conditional visibility
  const { selectedSecVisibleAtt: attributesWithValues, hiddenAttributes } =
    useMemo(() => {
      const selectedAttributes =
        attributes?.map((attr) => ({
          ...attr,
          // set `value` for each attribute:
          // 1. Prefer value from syncAttributes if available
          // 2. Else, take from attributeValues (initial values passed as props)
          // 3. If neither exists, default to an empty string ''
          value:
            syncAttributes?.[attr?.id] ?? attributeValues?.[attr?.id] ?? '',
        })) || [];

      /**
       * on initial render, `allFormValues` will be empty.
       * it is guaranteed that `allFormValues` is never an empty object after initialization,
       * so return early when it is empty.
       */
      if (isValueEmpty(allFormValues)) {
        return {
          // show all attributes if we are not getting allFormValues
          selectedSecVisibleAtt: selectedAttributes,
          hiddenAttributes: [],
        };
      }

      const {
        visibleAttributes: selectedSecVisibleAtt,
        hiddenAttributes: _allHiddenAttributes,
      } = getVisibleAttributes(selectedAttributes, allFormValues);

      return {
        selectedSecVisibleAtt,
        hiddenAttributes: _allHiddenAttributes,
      };
    }, [attributes, attributeValues, syncAttributes, allFormValues]);

  // Create dynamic validation schema for justifications
  const justificationValidationSchema = useMemo(() => {
    const schema = {};
    const hiddenIds = new Set(hiddenAttributes?.map((attr) => attr.id) || []);

    attributes.forEach((attr) => {
      // Skip required validation for hidden attributes
      if (hiddenIds.has(attr.id)) {
        schema[attr.id] = Yup.string().max(
          200,
          'Maximum 200 characters allowed'
        );
      } else {
        schema[attr.id] = Yup.string()
          .required('Justification is required')
          .max(200, 'Maximum 200 characters allowed');
      }
    });
    return Yup.object().shape(schema);
  }, [attributes, hiddenAttributes]);

  // Justification form
  const justificationFormikForm = useFormik({
    initialValues: value?.justifications || {},
    validationSchema: justificationValidationSchema,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  // Update justification form when attributes change
  useEffect(() => {
    const initialJustifications = {};
    attributes.forEach((attr) => {
      // Preserve values from value?.justifications or existing form values
      initialJustifications[attr.id] =
        value?.justifications?.[attr.id] ||
        justificationFormikForm.values[attr.id] ||
        '';
    });
    justificationFormikForm.setValues(initialJustifications);
  }, [attributes, value?.justifications]);

  const extractDepandantHiddenAttributes = useCallback(
    (_allHiddenAttributes, currentAttribute) => {
      if (!_allHiddenAttributes?.length || !currentAttribute?.id) {
        return [];
      }

      const dependentAttributes = [];

      _allHiddenAttributes.forEach((hiddenAttr) => {
        if (hiddenAttr?.conditional_formula?.rules?.length) {
          const hasMatchingRule = hiddenAttr.conditional_formula.rules.some(
            (rule) => rule.attribute_id === currentAttribute.id
          );

          if (hasMatchingRule) {
            dependentAttributes.push(hiddenAttr);
          }
        }
      });

      return dependentAttributes;
    },
    []
  );

  const handleAddAttribute = useCallback(() => {
    if (
      !attributeToBeAdded ||
      !templateStructure ||
      !userAddedTemplateStructure
    ) {
      return;
    }

    const attributeId = attributeToBeAdded.id;
    let attributeData = null;
    let belongsToSection = null;

    // Check if attribute belongs to a section in ORIGINAL template
    templateStructure.sections?.forEach((section) => {
      const foundAttr = section.attributes?.find(
        (attr) => attr.id === attributeId
      );
      if (foundAttr) {
        attributeData = foundAttr;
        belongsToSection = section.section_id;
      }
    });

    // If not found in sections, check basic attributes in ORIGINAL template
    if (!attributeData) {
      attributeData = templateStructure.attributes?.find(
        (attr) => attr.id === attributeId
      );
    }

    if (!attributeData) {
      console.error('Attribute not found in template structure');
      return;
    }

    // Preserve current form values before updating structure
    const preservedFormValues = entityDetailsFormikForm?.values
      ? { ...entityDetailsFormikForm.values }
      : {};

    // Build structure with only user-added attributes in the correct location
    setUserAddedTemplateStructure((prevStructure) => {
      const updatedStructure = JSON.parse(JSON.stringify(prevStructure));

      if (belongsToSection) {
        // Find or create the section
        let targetSection = updatedStructure.sections.find(
          (s) => s.section_id === belongsToSection
        );

        if (!targetSection) {
          // Get original section details from templateStructure
          const originalSection = templateStructure.sections.find(
            (s) => s.section_id === belongsToSection
          );

          targetSection = { ...originalSection, attributes: [] };

          updatedStructure.sections.push(targetSection);
        }

        // Add attribute to section if not already present
        const attrExists = targetSection.attributes.some(
          (attr) => attr.id === attributeId
        );
        if (!attrExists) {
          targetSection.attributes.push(attributeData);
        }
      } else {
        // Add to basic attributes if not already present
        const attrExists = updatedStructure.attributes.some(
          (attr) => attr.id === attributeId
        );
        if (!attrExists) {
          updatedStructure.attributes.push(attributeData);
        }
      }

      return updatedStructure;
    });
    const allDependantHiddenAttributes = extractDepandantHiddenAttributes(
      allHiddenAttributes,
      attributeToBeAdded
    );
    // add attribute to the list of attributes to be shown in the form of table
    setAttributes((prev) => {
      const existingIds = new Set(prev.map((attr) => attr.id));
      const newAttributes = [
        attributeToBeAdded,
        ...allDependantHiddenAttributes,
      ].filter((attr) => !existingIds.has(attr.id));
      return [...prev, ...newAttributes];
    });
    setAttributeToBeAdded(null);
    const alreadyAddedAttributes = new Set(
      [...attributes, attributeToBeAdded, ...allDependantHiddenAttributes].map(
        (attr) => attr.id
      )
    );
    // filter out already added attributes from the options list
    setAttributeOptions((prev) =>
      prev.filter((attr) => !alreadyAddedAttributes.has(attr.id))
    );
    setPreviousAttributeValue((pre) => {
      const newValues = {
        ...pre,
        [attributeToBeAdded?.id]:
          prevAttributeDetails?.[attributeToBeAdded?.id],
      };

      // Store previous values for all dependent hidden attributes
      allDependantHiddenAttributes.forEach((hiddenAttr) => {
        newValues[hiddenAttr.id] = prevAttributeDetails?.[hiddenAttr.id];
      });

      return newValues;
    });

    // Restore form values after structure update
    setAttributeDetails((prev) => ({
      ...prev,
      ...preservedFormValues,
    }));

    setAttributeValues((prev) => ({
      ...prev,
      ...preservedFormValues,
    }));
  }, [
    attributeToBeAdded,
    attributes,
    templateStructure,
    userAddedTemplateStructure,
    prevAttributeDetails,
    allHiddenAttributes,
    setUserAddedTemplateStructure,
    setPreviousAttributeValue,
    setAttributeOptions,
    setAttributeToBeAdded,
    setAttributes,
    entityDetailsFormikForm,
  ]);

  useEffect(() => {
    // fetch template attribute details and attributes values when entityDetails is available
    if (
      !isValueEmpty(entityDetails?.entity_id) &&
      openActionModal &&
      !isFirstOpenRef.current // this will prevent API calls when we open and close the modal multiple times, API calls will be made only first time when we open the modal
    ) {
      handleGetTemplateDetails();
      handleGetAttributesData();
      isFirstOpenRef.current = true;
    }
  }, [entityDetails, openActionModal]);

  useEffect(() => {
    constraintCallChainRef.current = constraintCallChain;
  }, [constraintCallChain]);

  // Initialize attributes from value.currentValue
  useEffect(() => {
    if (
      !value?.currentValue ||
      !templateStructure ||
      !attributeOptions?.length
    ) {
      return;
    }

    const attributeIds = Object.keys(value.currentValue);
    if (!attributeIds.length) {
      return;
    }

    // Find attributes from attributeOptions based on IDs
    const attributesToAdd = attributeOptions.filter((option) =>
      attributeIds.includes(String(option.id))
    );

    // Remove duplicates based on attribute id
    const uniqueAttributesToAdd = Array.from(
      new Map(attributesToAdd.map((attr) => [attr.id, attr])).values()
    );

    if (!uniqueAttributesToAdd.length) {
      return;
    }

    // Build userAddedTemplateStructure with the attributes
    setUserAddedTemplateStructure((prevStructure) => {
      const updatedStructure = JSON.parse(JSON.stringify(prevStructure));

      uniqueAttributesToAdd.forEach((attributeToAdd) => {
        const attributeId = attributeToAdd.id;
        let attributeData = null;
        let belongsToSection = null;

        // Check if attribute belongs to a section in ORIGINAL template
        templateStructure.sections?.forEach((section) => {
          const foundAttr = section.attributes?.find(
            (attr) => attr.id === attributeId
          );
          if (foundAttr) {
            attributeData = foundAttr;
            belongsToSection = section.section_id;
          }
        });

        // If not found in sections, check basic attributes in ORIGINAL template
        if (!attributeData) {
          attributeData = templateStructure.attributes?.find(
            (attr) => attr.id === attributeId
          );
        }

        if (!attributeData) {
          return;
        }

        if (belongsToSection) {
          // Find or create the section
          let targetSection = updatedStructure.sections.find(
            (s) => s.section_id === belongsToSection
          );

          if (!targetSection) {
            const originalSection = templateStructure.sections.find(
              (s) => s.section_id === belongsToSection
            );
            targetSection = {
              ...originalSection,
              attributes: [],
            };
            updatedStructure.sections.push(targetSection);
          }

          // Add attribute if not already present
          const attrExists = targetSection.attributes.some(
            (attr) => attr.id === attributeId
          );
          if (!attrExists) {
            targetSection.attributes.push(attributeData);
          }
        } else {
          // Add to basic attributes if not already present
          const attrExists = updatedStructure.attributes.some(
            (attr) => attr.id === attributeId
          );
          if (!attrExists) {
            updatedStructure.attributes.push(attributeData);
          }
        }
      });

      return updatedStructure;
    });

    // Set the attributes to display in the table
    setAttributes(uniqueAttributesToAdd);

    // Set current attribute values for formik form
    setAttributeDetails((prev) => ({
      ...prev,
      ...value.currentValue,
    }));

    // Set attribute values for the table display
    setAttributeValues((prev) => ({
      ...prev,
      ...value.currentValue,
    }));

    // Set previous attribute values
    if (Object.keys(value?.previousValue)?.length) {
      setPreviousAttributeValue(value.previousValue);
      setPrevAttributeDetails((pre) => {
        return { ...pre, ...value.previousValue };
      });
    } else {
      // If previousValue not provided, use values from API for the added attributes
      const previousValuesFromApi = {};
      uniqueAttributesToAdd.forEach((attr) => {
        previousValuesFromApi[attr.id] = prevAttributeDetails?.[attr.id];
      });
      setPreviousAttributeValue(previousValuesFromApi);
    }

    // Remove added attributes from options
    const addedIds = new Set(attributeIds.map(Number));
    setAttributeOptions((prev) =>
      prev.filter((attr) => !addedIds.has(attr.id))
    );
  }, [value?.currentValue, templateStructure]);

  // Store the current values in a ref to track latest changes
  const currentValuesRef = useRef({});

  const { getConstraintSources, getConstraints, getAttributeSourceMapping } =
    useAttribute();

  // Update entityDetailsFormikForm when attributeDetails change
  useEffect(() => {
    if (
      entityDetailsFormikForm &&
      entityDetailsFormikForm.setValues &&
      value?.currentValue &&
      Object.keys(value.currentValue).length > 0
    ) {
      // Merge current values with existing form values
      entityDetailsFormikForm.setValues(
        {
          ...entityDetailsFormikForm.values,
          ...value.currentValue,
        },
        false // Don't validate on set
      );
    }
  }, [value?.currentValue]);

  const handleSaveOtherDetailsFormikForm = async () => {
    // Validate justification form first
    const justificationErrors = await justificationFormikForm.validateForm();

    if (Object.keys(justificationErrors).length > 0) {
      // Mark all fields as touched to show errors
      const touchedFields = {};
      Object.keys(justificationErrors).forEach((key) => {
        touchedFields[key] = true;
      });
      justificationFormikForm.setTouched(touchedFields);
      throw new Error('Justification validation failed');
    }

    // If justification validation passes, call the entity form save
    const payload = await handleSaveOtherDetailsForm();

    // filter by visible tracked rows instead of excluding hidden IDs
    const visibleTrackedIds = new Set(
      (attributesWithValues || []).map((attr) => String(attr?.id))
    );

    // Filter out hidden attributes from payload
    const filteredPayload = {};
    Object.keys(payload || {}).forEach((key) => {
      if (visibleTrackedIds.has(String(key))) {
        filteredPayload[key] = payload[key];
      }
    });

    // Filter out hidden attributes from justifications
    const filteredJustifications = {};
    Object.keys(justificationFormikForm.values || {}).forEach((key) => {
      if (visibleTrackedIds.has(String(key))) {
        filteredJustifications[key] = justificationFormikForm.values[key];
      }
    });

    // Return both attribute data and justifications (filtered)
    return {
      attributeData: filteredPayload,
      justifications: filteredJustifications,
    };
  };

  const handleDeleteAttribute = useCallback(
    (attributeToDelete) => {
      if (!attributeToDelete) {
        return;
      }

      const attributeId = attributeToDelete.id;

      // Preserve current form values before updating structure (excluding deleted attribute)
      const preservedFormValues = entityDetailsFormikForm?.values
        ? { ...entityDetailsFormikForm.values }
        : {};
      delete preservedFormValues[attributeId];
      delete preservedFormValues[String(attributeId)];

      // Remove from userAddedTemplateStructure
      setUserAddedTemplateStructure((prevStructure) => {
        const updatedStructure = JSON.parse(JSON.stringify(prevStructure));

        // Remove from sections and filter out empty sections
        updatedStructure.sections = updatedStructure.sections
          .map((section) => ({
            ...section,
            attributes: section.attributes.filter(
              (attr) => attr.id !== attributeId
            ),
          }))
          .filter((section) => section.attributes.length > 0);

        // Remove from basic attributes
        updatedStructure.attributes = updatedStructure.attributes.filter(
          (attr) => attr.id !== attributeId
        );

        return updatedStructure;
      });

      // Remove from attributes list
      setAttributes((prev) => prev.filter((attr) => attr.id !== attributeId));

      // Add back to attributeOptions
      setAttributeOptions((prev) => {
        const attributeExists = prev.some((attr) => attr.id === attributeId);
        if (!attributeExists) {
          return [...prev, attributeToDelete];
        }
        return prev;
      });

      // Remove from previousAttributeValue
      setPreviousAttributeValue((prev) => {
        const newValues = { ...prev };
        delete newValues[attributeId];
        delete newValues[String(attributeId)];
        return newValues;
      });

      // Remove from attributeDetails
      setAttributeDetails((prev) => {
        const newDetails = { ...prev };
        delete newDetails[attributeId];
        delete newDetails[String(attributeId)];
        return newDetails;
      });

      // Remove from attributeValues
      setAttributeValues((prev) => {
        const newValues = { ...prev };
        delete newValues[attributeId];
        delete newValues[String(attributeId)];
        return newValues;
      });

      // Remove from entityDetailsFormikForm
      if (entityDetailsFormikForm?.setValues) {
        const currentFormValues = { ...entityDetailsFormikForm.values };
        delete currentFormValues[attributeId];
        delete currentFormValues[String(attributeId)];
        entityDetailsFormikForm.setValues(currentFormValues, false);

        // Clear errors for the deleted attribute
        if (entityDetailsFormikForm.setErrors) {
          const currentErrors = { ...entityDetailsFormikForm.errors };
          delete currentErrors[attributeId];
          delete currentErrors[String(attributeId)];
          entityDetailsFormikForm.setErrors(currentErrors);
        }

        // Clear touched state for the deleted attribute
        if (entityDetailsFormikForm.setTouched) {
          const currentTouched = { ...entityDetailsFormikForm.touched };
          delete currentTouched[attributeId];
          delete currentTouched[String(attributeId)];
          entityDetailsFormikForm.setTouched(currentTouched, false);
        }
      }

      // Remove from justifications
      const currentJustifications = { ...justificationFormikForm.values };
      delete currentJustifications[attributeId];
      delete currentJustifications[String(attributeId)];
      justificationFormikForm.setValues(currentJustifications, false);

      // Clear justification errors and touched state
      const currentJustificationErrors = {
        ...justificationFormikForm.errors,
      };
      delete currentJustificationErrors[attributeId];
      delete currentJustificationErrors[String(attributeId)];
      justificationFormikForm.setErrors(currentJustificationErrors);

      const currentJustificationTouched = {
        ...justificationFormikForm.touched,
      };
      delete currentJustificationTouched[attributeId];
      delete currentJustificationTouched[String(attributeId)];
      justificationFormikForm.setTouched(currentJustificationTouched, false);

      // Restore form values after structure update (excluding deleted attribute)
      setAttributeDetails((prev) => ({
        ...prev,
        ...preservedFormValues,
      }));

      setAttributeValues((prev) => ({
        ...prev,
        ...preservedFormValues,
      }));
    },
    [
      setUserAddedTemplateStructure,
      setAttributes,
      setAttributeOptions,
      setPreviousAttributeValue,
      setAttributeDetails,
      setAttributeValues,
      justificationFormikForm,
      entityDetailsFormikForm,
      templateStructure,
    ]
  );

  const getAttributeMapping = useCallback(() => {
    setLoading(true);
    const payload = { entity_id: entityId, entity_type: entityType };

    getAttributeSourceMapping(payload)
      .then((res) => {
        if (res) {
          setAttributeSourceMapping(res?.data || {});
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    entityId,
    entityType,
    setAttributeSourceMapping,
    getAttributeSourceMapping,
    setLoading,
  ]);

  useEffect(() => {
    if (entityId && entityType) {
      getAttributeMapping();
    }
  }, [entityId, entityType]);

  // Debounce timer ref for constraint API calls
  const debounceTimerRef = useRef(null);

  // validate constraint values against new options
  const validateConstraintValues = useCallback(
    (newOverrideOptions, { persistDraft = false } = {}) => {
      // Iterate through each attribute that has new constraint options
      Object.keys(newOverrideOptions).forEach((attrId) => {
        const currentValue = entityDetailsFormikForm?.values?.[attrId];
        const newOptions = newOverrideOptions[attrId];

        // Only validate if there's a current value and new options are provided
        if (
          !isValueEmpty(currentValue) &&
          newOptions &&
          Array.isArray(newOptions)
        ) {
          // Case 1: No valid options exist - clear the field value
          if (newOptions.length === 0) {
            entityDetailsFormikForm?.setFieldValue(attrId, null);
            if (persistDraft && handleAutoSave) {
              handleAutoSave(attrId, null);
            }
            return;
          }

          // Extract valid option values, handling both object and primitive formats
          const validOptionValues = newOptions.map((option) =>
            typeof option === 'object'
              ? option?.value || option?.entity_id
              : option
          );

          // Case 2: Multi-select field - remove invalid selections, keep valid ones
          if (Array.isArray(currentValue)) {
            // Filter out values that are no longer in the valid options list
            const invalidValues = currentValue?.filter(
              (val) =>
                !validOptionValues.includes(val?.value || val?.entity_id || val)
            );

            // Only update if there are invalid values to remove
            if (invalidValues.length > 0) {
              // Keep only the valid selections
              const validValues = currentValue.filter((val) =>
                validOptionValues.includes(val?.value || val?.entity_id || val)
              );

              // Set to valid values array or null if no valid selections remain
              const correctValue = validValues.length > 0 ? validValues : null;

              entityDetailsFormikForm?.setFieldValue(attrId, correctValue);

              if (persistDraft && handleAutoSave) {
                handleAutoSave(attrId, correctValue);
              }
            }
          } else {
            // Case 3: Single-select field - clear if current value is invalid
            // Check validity for both object format (currentValue.value) and primitive format
            const isCurrentValueValid = validOptionValues.includes(
              currentValue?.value || currentValue?.entity_id || currentValue
            );

            // Clear the field if current selection is no longer valid
            if (!isCurrentValueValid) {
              entityDetailsFormikForm?.setFieldValue(attrId, null);

              if (persistDraft && handleAutoSave) {
                handleAutoSave(attrId, null);
              }
            }
          }
        }
      });
    },
    [entityDetailsFormikForm, handleAutoSave]
  );

  // Debounced constraint API call for value changes
  const handleConstraintValueChange = useCallback(
    async (sourceAttributeId, newValue, oldValue, isAutoPopulated = false) => {
      // Store original value for error handling
      setOriginalValues((prev) => ({
        ...prev,
        [sourceAttributeId]: oldValue,
      }));

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounced timer
      debounceTimerRef.current = setTimeout(async () => {
        try {
          setIsLoadingConstraints(true);
          const _allFormValues = {
            ...attributeDetails,
            ...syncAttributes,
            ...(entityDetailsFormikForm?.values || {}),
            [sourceAttributeId]: newValue,
          };

          const response = await getConstraints({
            entity_id: entityId,
            entity_type: entityData?.entity_type,
            source_attribute_id: sourceAttributeId,
            form_data: _allFormValues,
          });

          if (response?.data?.lookup) {
            const newOverrideOptions = response.data.lookup;

            // Update override options
            setOverrideDropdownOptions((prev) => ({
              ...prev,
              ...newOverrideOptions,
            }));

            // Validate constraint values
            validateConstraintValues(newOverrideOptions, {
              persistDraft: true,
            });
          }

          // Handle auto population from constraint API response
          if (response?.data?.autopupulate) {
            // eslint-disable-next-line no-use-before-define
            handleAutoPopulation &&
              // eslint-disable-next-line no-use-before-define
              handleAutoPopulation(response.data.autopupulate);
          }

          // Clear constraint call chain after processing response
          if (isAutoPopulated) {
            const attrIdNum = Number.parseInt(sourceAttributeId);
            setConstraintCallChain((prev) => {
              const newChain = new Set(prev);
              newChain.delete(attrIdNum);
              return newChain;
            });
            constraintCallChainRef.current.delete(attrIdNum);
          }

          // Handle date calendar constraints
          if (response?.data?.date_calander) {
            const newDateCalendarConstraints = response.data.date_calander;

            // Update date calendar constraints
            setDateCalendarConstraints((prev) => ({
              ...prev,
              ...newDateCalendarConstraints,
            }));

            // Dynamically update Yup form schema with new date constraints
            if (setFormSchema) {
              setFormSchema((prevSchema) => {
                if (!prevSchema) {
                  return prevSchema;
                }
                return applyDateCalendarConstraints(
                  prevSchema,
                  newDateCalendarConstraints
                );
              });
            }
          }

          // Handle mandatory constraints
          if (response?.data?.mandatory) {
            const newMandatoryConstraints = response.data.mandatory;

            setMandatoryConstraints((prev) => ({
              ...prev,
              ...newMandatoryConstraints,
            }));

            // Dynamically update Yup form schema with new mandatory constraints
            if (setFormSchema) {
              setFormSchema((prevSchema) => {
                if (!prevSchema) {
                  return prevSchema;
                }
                return applyMandatoryConstraints(
                  prevSchema,
                  newMandatoryConstraints,
                  attributesWithValues
                );
              });
            }
          }
        } catch (error) {
          console.error('Error fetching constraints for value change:', error);

          // Revert to original value on error
          const originalValue = originalValues[sourceAttributeId];
          if (originalValue !== undefined) {
            entityDetailsFormikForm?.setFieldValue(
              sourceAttributeId,
              originalValue
            );
          }

          // Clear constraint call chain on error
          if (isAutoPopulated) {
            const attrIdNum = Number.parseInt(sourceAttributeId);
            setConstraintCallChain((prev) => {
              const newChain = new Set(prev);
              newChain.delete(attrIdNum);
              return newChain;
            });
            constraintCallChainRef.current.delete(attrIdNum);
          }
        } finally {
          setIsLoadingConstraints(false);
        }
      }, 500); // 500ms debounce
    },
    [
      entityId,
      entityData?.entity_type,
      attributeDetails,
      syncAttributes,
      entityDetailsFormikForm,
      getConstraints,
      handleAutoSave,
      originalValues,
      validateConstraintValues,
    ]
  );

  // Helper function to handle auto population
  const handleAutoPopulation = useCallback(
    (autoPopulateData) => {
      Object.keys(autoPopulateData || {}).forEach((attrId) => {
        const autoPopulatedValue = autoPopulateData[attrId];
        const currentValue = entityDetailsFormikForm?.values?.[attrId];

        // Only auto populate if the new value is not empty/null, or if current value is empty
        if (!isValueEmpty(autoPopulatedValue) || isValueEmpty(currentValue)) {
          const valueToSet = !isValueEmpty(autoPopulatedValue)
            ? autoPopulatedValue
            : currentValue;

          // Skip if value hasn't actually changed to prevent unnecessary API calls
          if (currentValue === valueToSet) {
            return;
          }

          // Update both Formik and local state without triggering change handlers
          entityDetailsFormikForm?.setFieldValue(attrId, valueToSet);
          setAttributeValues((prev) => ({
            ...prev,
            [attrId]: valueToSet,
          }));

          const attrIdNum = Number.parseInt(attrId);
          // Check if auto populated attribute is a constraint source and trigger API call
          // Use ref to get latest value and avoid stale closure issues
          if (
            autoCompleteSourceAttributes.includes(attrIdNum) &&
            !constraintCallChainRef.current.has(attrIdNum)
          ) {
            // Add to call chain to prevent cycles
            setConstraintCallChain((prev) => {
              const newSet = new Set(prev);
              newSet.add(attrIdNum);
              return newSet;
            });
            constraintCallChainRef.current.add(attrIdNum);

            // Trigger constraint API call for this auto populated value
            const timerId = setTimeout(() => {
              handleConstraintValueChange(
                attrId,
                valueToSet,
                currentValue,
                true
              );
            }, 100);

            // Track timer for cleanup
            autoPopulateTimersRef.current.push(timerId);
          }
        }
      });
    },
    [
      entityDetailsFormikForm,
      setAttributeValues,
      autoCompleteSourceAttributes,
      handleConstraintValueChange,
    ]
  );

  // Fetch constraint sources on edit mode entry
  const fetchConstraintSources = useCallback(async () => {
    if (!entityId || !entityData?.entity_type) return;

    try {
      const response = await getConstraintSources({
        entity_id: entityId,
        entity_type: entityData?.entity_type,
      });

      if (response?.data?.lookup_source_attributes) {
        setLookupSourceAttributes(response.data.lookup_source_attributes);
      }
      if (response?.data?.autocomplete_source_attributes) {
        setAutoCompleteSourceAttributes(
          response.data.autocomplete_source_attributes
        );
      }
      if (response?.data?.date_calander_attributes) {
        setDateCalendarSourceAttributes(response.data.date_calander_attributes);
      }
      if (response?.data?.mandatory_source_attributes) {
        setMandatorySourceAttributes(response.data.mandatory_source_attributes);
      }
    } catch (error) {
      console.error('Error fetching constraint sources:', error);
    }
  }, [entityId, entityData?.entity_type, getConstraintSources]);

  // Fetch initial constraints when edit mode starts
  const fetchInitialConstraints = useCallback(async () => {
    if (!entityId || !entityData?.entity_type || !isMultipleEditMode) return;

    try {
      setIsLoadingConstraints(true);
      const _allFormValues = {
        ...attributeDetails,
        ...syncAttributes,
        ...(entityDetailsFormikForm?.values || {}),
      };

      const response = await getConstraints({
        entity_id: entityId,
        entity_type: entityData?.entity_type,
        form_data: _allFormValues,
      });

      if (response?.data?.lookup) {
        const newOverrideOptions = response.data.lookup;
        setOverrideDropdownOptions(newOverrideOptions);
        validateConstraintValues(newOverrideOptions);
      }

      // Handle autopopulation from initial constraints
      if (response?.data?.autopupulate) {
        handleAutoPopulation(response.data.autopupulate);
      }
      // Handle date calendar constraints on initial load
      if (response?.data?.date_calander) {
        const newDateCalendarConstraints = response.data.date_calander;

        // Update date calendar constraints
        setDateCalendarConstraints((prev) => ({
          ...prev,
          ...newDateCalendarConstraints,
        }));

        // Dynamically update Yup form schema with new date constraints
        if (setFormSchema) {
          setFormSchema((prevSchema) => {
            if (!prevSchema) {
              return prevSchema;
            }
            return applyDateCalendarConstraints(
              prevSchema,
              newDateCalendarConstraints
            );
          });
        }
      }

      // Handle mandatory constraints on initial load
      if (response?.data?.mandatory) {
        const newMandatoryConstraints = response.data.mandatory;

        setMandatoryConstraints((prev) => ({
          ...prev,
          ...newMandatoryConstraints,
        }));

        // Dynamically update Yup form schema with new mandatory constraints
        if (setFormSchema) {
          setFormSchema((prevSchema) => {
            if (!prevSchema) {
              return prevSchema;
            }
            return applyMandatoryConstraints(
              prevSchema,
              newMandatoryConstraints,
              attributesWithValues
            );
          });
        }
      }
    } catch (error) {
      console.error('Error fetching initial constraints:', error);
    } finally {
      setIsLoadingConstraints(false);
    }
  }, [
    entityId,
    entityData?.entity_type,
    attributeDetails,
    syncAttributes,
    entityDetailsFormikForm?.values,
    isMultipleEditMode,
    getConstraints,
    handleAutoPopulation,
    validateConstraintValues,
    setFormSchema,
    applyDateCalendarConstraints,
  ]);

  // Hook to detect when constraint source attributes change
  const handleAttributeValueChange = useCallback(
    (attributeId, newValue, oldValue) => {
      // Check if this attribute is a lookup source attribute
      if (
        lookupSourceAttributes.includes(Number.parseInt(attributeId)) ||
        autoCompleteSourceAttributes.includes(Number.parseInt(attributeId)) ||
        dateCalendarSourceAttributes.includes(Number.parseInt(attributeId)) ||
        mandatorySourceAttributes.includes(Number.parseInt(attributeId))
      ) {
        handleConstraintValueChange(attributeId, newValue, oldValue);
      }
    },
    [
      lookupSourceAttributes,
      autoCompleteSourceAttributes,
      dateCalendarSourceAttributes,
      mandatorySourceAttributes,
      handleConstraintValueChange,
    ]
  );

  // Fetch constraint sources when edit mode starts
  useEffect(() => {
    if (isMultipleEditMode && entityId && entityData?.entity_type) {
      fetchConstraintSources();
    }
  }, [isMultipleEditMode, entityId, entityData?.entity_type]);

  // Fetch initial constraints after constraint sources are loaded
  useEffect(() => {
    if (
      lookupSourceAttributes?.length > 0 ||
      autoCompleteSourceAttributes?.length > 0 ||
      dateCalendarSourceAttributes?.length > 0 ||
      mandatorySourceAttributes?.length > 0
    ) {
      fetchInitialConstraints();
    }
  }, [
    lookupSourceAttributes,
    autoCompleteSourceAttributes,
    dateCalendarSourceAttributes,
    mandatorySourceAttributes,
  ]);
  // Cleanup debounce timer and auto-populate timers
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Clear all auto-populate timers
      autoPopulateTimersRef.current.forEach((timerId) => {
        clearTimeout(timerId);
      });
      autoPopulateTimersRef.current = [];
      // Reset constraint call chain ref
      constraintCallChainRef.current = new Set();
    };
  }, []);

  // Stable handler for form field changes
  const handleFieldChange = useCallback(
    (attributeId, newValue) => {
      const oldValue = entityDetailsFormikForm?.values?.[attributeId];
      setSkipPageReset(true);

      if (entityDetailsFormikForm) {
        // Store the new value in ref immediately
        currentValuesRef.current[attributeId] = newValue;
        entityDetailsFormikForm.setFieldValue(attributeId, newValue);
        if (isMultipleEditMode) {
          handleAutoSave(attributeId, newValue, { persist: false });
        }
      }

      // Handle constraint value changes
      if (handleAttributeValueChange) {
        handleAttributeValueChange(attributeId, newValue, oldValue);
      }
      if (
        isHDFCUAMEnabled &&
        HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES.includes(
          currentModelStatus?.currentState
        )
      ) {
        // Get all attributes from template to find the current attribute
        const allAttributesList = extractAllAttributesFromTemplate(formDetails);
        const currentAttribute = allAttributesList?.find(
          (attr) => attr?.id === attributeId
        );

        // Validate deployment date
        validateDeploymentDate({
          attribute: currentAttribute,
          newValue,
          formDetails,
          attributeValues,
        });

        // Validate deployment date communication delay
        validateDeploymentDateCommunication({
          attribute: currentAttribute,
          newValue,
          formDetails,
          attributeValues,
        });
      }
    },
    [
      entityDetailsFormikForm,
      isMultipleEditMode,
      handleAutoSave,
      handleAttributeValueChange,
      formDetails,
      syncAttributes,
      attributeValues,
      isHDFCUAMEnabled,
      currentModelStatus,
    ]
  );

  /**
   * we need this useEffect to reset the values of hidden attributes
   * whenever hidden attributes changes and are different from previous hidden attributes
   * we will reset the values of hidden attributes to their initial values
   */
  useEffect(() => {
    /**
     * on initial render we will be getting empty initialValues inside entityDetailsFormikForm
     */
    if (
      !entityDetailsFormikForm?.setFieldValue ||
      !entityDetailsFormikForm?.initialValues ||
      Object.keys(entityDetailsFormikForm?.initialValues).length === 0
    ) {
      return;
    }

    const currentHiddenIds = new Set(hiddenAttributes.map((a) => a.id));
    const prevHiddenIds = prevHiddenIdsRef.current;

    // if hidden sets are equal, do nothing
    // need this to prevent infinite loop
    if (areSetsEqual(prevHiddenIds, currentHiddenIds)) return;

    const newInitialValues = createInitialFormValues(hiddenAttributes);

    // if hidden att set changed -> reset the values of hidden attributes to initial values
    hiddenAttributes.forEach((attr) => {
      const newValue = newInitialValues[attr.id] || null;
      entityDetailsFormikForm.setFieldValue(attr.id, newValue);
    });

    // if code flow reaches here, means hidden sets have changed, update the ref to latest hidden att ids
    prevHiddenIdsRef.current = currentHiddenIds;
  }, [hiddenAttributes, entityDetailsFormikForm]);

  const derivedMapping = useMemo(() => {
    const map = {};
    attributesWithValues?.forEach((attr) => {
      map[attr.id] = Boolean(attr?.is_derived);
    });
    return map;
  }, [attributesWithValues]);

  // Create a ref to store the current context to avoid dependency issues
  const cellContextRef = useRef({});

  // Update the ref with current values
  cellContextRef.current = {
    mode,
    isMultipleEditMode,
    entityDetailsFormikForm,
    usersList,
    teamUsersList,
    groupList,
    entityData,
    handleFieldChange,
    isUserAuthorizedForUpdateAttribute,
    attributeSourceMapping,
    loading,
    isTableView,
    derivedMapping,
    isUserAuthorizedToOverrideAttribute,
    overrideData,

    handleAutoSave,
    hasDraftChanges,
    // Constraint-related props
    lookupSourceAttributes,
    autoCompleteSourceAttributes,
    overrideDropdownOptions,
    isLoadingConstraints,
    dateCalendarConstraints,
    dateCalendarSourceAttributes,
    mandatoryConstraints,
    groupDetails,
    // Justification form data
    justificationFormikForm,
    getJustificationErrorText,
    modelTemplateDetails,
  };

  // Stable cell renderer that uses ref to access current context
  const renderPreviousValueCell = useCallback(
    (props) => {
      // eslint-disable-next-line react/prop-types
      const attributeId = props?.row?.original?.id;
      const currentCellAttribute = {
        // eslint-disable-next-line react/prop-types
        ...(props?.row?.original || {}),
        entityType: entityData?.entity_type,
        requestType: entityData?.request_type,
      };

      const displayValue =
        prevAttributeDetails?.[attributeId] ?? props?.value ?? '';
      const formattedDisplayValue = formatAttributeValue(
        displayValue,
        currentCellAttribute,
        false,
        entityData?.entity_id,
        entityDetailsFormikForm,
        usersList,
        groupList,
        entityData?.entity_type,
        undefined,
        false,
        false,
        entityData
      );
      const { ref: textRef, isOverflowed } = useTextOverflow(
        formattedDisplayValue
      );

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            '&:hover .edit-icon-button': {
              opacity: 1,
              visibility: 'visible',
            },
          }}
        >
          <Tooltip
            title={
              typeof formattedDisplayValue !== 'object' && isOverflowed
                ? formattedDisplayValue
                : ''
            }
          >
            <Typography
              ref={textRef}
              variant="body8"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
            >
              {formattedDisplayValue}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
    [prevAttributeDetails, formatAttributeValue, entityData]
  );

  const renderCurrentValueCell = useCallback((props) => {
    const context = cellContextRef.current;
    const editPermission = mode === 'view' ? false : true;
    // eslint-disable-next-line react/prop-types
    const attributeId = props?.row?.original?.id;
    const isDerived = context?.derivedMapping[attributeId] === true;

    return ValueCellRenderer(
      props,
      context?.handleEditClick,
      editPermission,
      context?.isMultipleEditMode,
      context?.entityDetailsFormikForm,
      context?.usersList,
      context?.teamUsersList,
      context?.groupList,
      context?.entityData,
      context?.handleFieldChange,
      context?.attributeSourceMapping,
      context?.loading,
      context?.isTableView,
      isDerived,
      context?.isUserAuthorizedToOverrideAttribute,
      context?.overrideData,
      context?.handleOpenDerivedModal,
      context?.handleAutoSave,
      // Constraint-related props
      context?.lookupSourceAttributes,
      context?.autoCompleteSourceAttributes,
      context?.overrideDropdownOptions,
      context?.isLoadingConstraints,
      context?.dateCalendarConstraints,
      context?.dateCalendarSourceAttributes,
      context?.hasDraftChanges,
      context?.groupDetails,
      context?.mandatoryConstraints,
      context?.modelTemplateDetails
    );
  }, []);

  // Memoize the Name Cell renderer function
  const renderNameCell = useCallback(
    (props) =>
      NameCellRenderer(
        props,
        allAttributeChats,
        handleOpenChat,
        handleAttributeClick,
        isTableView,
        mandatoryConstraints
      ),
    [
      allAttributeChats,
      handleAttributeClick,
      handleOpenChat,
      isTableView,
      mandatoryConstraints,
    ]
  );

  const renderJustificationCell = useCallback((props) => {
    const context = cellContextRef.current;
    // eslint-disable-next-line react/prop-types
    const attributeId = props?.row?.original?.id;
    const fieldName = String(attributeId);

    const justificationValue =
      context.justificationFormikForm.values[attributeId] || '';

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: CELL_STYLE_CONFIG.minHeight,
          display: 'flex',
          alignItems: 'center',
          '& .MuiFormControl-root': {
            marginTop: CELL_STYLE_CONFIG.formControl.marginTop,
            '& .MuiInputLabel-root': {
              display: CELL_STYLE_CONFIG.formControl.label.display,
            },
          },
        }}
      >
        {context.mode === 'view' ? (
          <Typography
            variant="body2"
            sx={{
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={justificationValue}
          >
            {justificationValue || '-'}
          </Typography>
        ) : (
          <JustificationInput
            attributeId={attributeId}
            fieldName={fieldName}
            justificationValue={justificationValue}
            context={context}
          />
        )}
      </Box>
    );
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Name',
          accessor: 'label',
          Cell: renderNameCell,
        },
        {
          Header: 'Current Value',
          accessor: 'previousValue',
          Cell: renderPreviousValueCell,
        },
        {
          Header: 'Updated Value',
          accessor: 'currentValue',
          Cell: renderCurrentValueCell,
        },
        !value?.disableEdit && {
          Header: 'Justification',
          accessor: 'justification',
          Cell: renderJustificationCell,
        },
      ].filter(Boolean),
    [
      renderNameCell,
      prevAttributeDetails,
      entityData,
      renderCurrentValueCell,
      renderJustificationCell,
      value,
    ]
  );

  const handleOnMenuExit = useCallback(() => {
    setSelectedRow(null);
  }, [setSelectedRow]);

  const menus = useMemo(() => {
    if (mode === 'view') {
      return [];
    }

    return [
      {
        label: 'Delete attribute',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          // eslint-disable-next-line react/prop-types
          handleDeleteAttribute(props?.original);
        },
        isMenuDisabled: false,
      },
    ];
  }, [mode, handleDeleteAttribute]);

  const handleGetCellProps = useCallback(
    (cell) => {
      if (mode === 'view') {
        return {
          style: {
            height: 'none',
            padding: '20px !important',
          },
        };
      }

      return {
        onContextMenu: (e) => {
          e.preventDefault();
          setSelectedRow(cell?.row);
          showRowLevelMenus({
            id: 'attribute-table-menus',
            event: e,
            props: cell?.row,
          });
        },
        style: {
          height: 'none',
          padding: '20px !important',
          backgroundColor:
            cell?.row?.original?.id === selectedRow?.original?.id
              ? theme?.palette?.action?.hover
              : 'inherit',
          cursor: mode === 'edit' ? 'context-menu' : 'default',
        },
      };
    },
    [mode, showRowLevelMenus, selectedRow, theme]
  );

  useEffect(() => {
    setAttributeValues((prevValues) => ({
      ...prevValues,
      ...attributeDetails,
    }));
  }, [attributeDetails]);
  const handleImpact = useCallback(() => {
    const impactPayload = {
      currentValue: value?.currentValue || EMPTY_OBJECT,
      previousValue: value?.previousValue || EMPTY_OBJECT,
      justifications: value?.justifications || EMPTY_OBJECT,
      disableEdit: true,
      entity_id: entityDetails?.entity_id,
      entity_type: impactEntityType,
    };

    if (
      isValueEmpty(impactPayload?.entity_id) ||
      isValueEmpty(impactPayload?.entity_type)
    ) {
      SwalToast({
        icon: 'error',
        title: 'Unable to fetch impact. Entity details are missing.',
      });
      return;
    }

    if (Object.keys(impactPayload?.previousValue || {}).length === 0) {
      SwalToast({
        icon: 'warning',
        title: 'No changed attributes available for impact analysis.',
      });
      return;
    }

    setImpactLoading(true);

    getImpactedAttributeData(impactPayload)
      .then((response) => {
        setImpactedData(response?.data || EMPTY_OBJECT);
        setIsImpactModalOpen(true);
      })
      .catch((error) => {
        console.error('Error fetching impacted attribute data:', error);

        SwalToast({
          icon: 'error',
          title:
            error?.response?.data?.message || 'Failed to fetch impact data.',
        });
      })
      .finally(() => {
        setImpactLoading(false);
      });
  }, [
    value?.currentValue,
    value?.previousValue,
    value?.justifications,
    entityDetails?.entity_id,
    impactEntityType,
    getImpactedAttributeData,
  ]);
  const handleCloseImpactModal = useCallback(() => {
    setIsImpactModalOpen(false);
    setImpactedData(EMPTY_OBJECT);
  }, []);
  const handleCloseActionModal = useCallback(() => {
    setOpenActionModal(false);
    setAttributeToBeAdded(null);
    entityDetailsFormikForm.resetForm();
  }, [setOpenActionModal, setAttributeToBeAdded, entityDetailsFormikForm]);

  const handleFieldClick = useCallback(() => {
    setOpenActionModal(true);
  }, [setOpenActionModal]);

  const handleSaveForm = useCallback(() => {
    handleSaveOtherDetailsFormikForm?.().then((data) => {
      const payload = {
        currentValue: data?.attributeData,
        previousValue: previousAttributeValue,
        justifications: data?.justifications,
      };
      onChange(payload);
      onDraftBlur?.();
      handleCloseActionModal();
    });
  }, [
    handleSaveOtherDetailsFormikForm,
    previousAttributeValue,
    onChange,
    onDraftBlur,
    handleCloseActionModal,
  ]);

  return (
    <Box position="relative">
      {mode === 'edit' && showLabel ? (
        <Box sx={{ position: 'relative', top: '4px' }}>{label}</Box>
      ) : null}
      <Box
        onClick={handleFieldClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '36px',
          cursor: 'pointer',
        }}
      >
        <Typography
          sx={{
            color: hasDraftChip
              ? theme?.palette?.draft?.accent
              : 'primary.main',
            textDecoration: 'underline',
          }}
          title={
            mode === 'view'
              ? `View ${attribute?.label || 'info'}`
              : `Update ${attribute?.label || 'info'}`
          }
          variant="body7"
        >
          {mode === 'view'
            ? `View ${attribute?.label || 'info'}`
            : `Update ${attribute?.label || 'info'}`}
        </Typography>
      </Box>
      {/* Action Selection Dialog */}
      <Dialog
        open={openActionModal}
        onClose={handleCloseActionModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{`${mode === 'view' ? 'View' : 'Update'} ${attribute?.label || 'Attribute'}`}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseActionModal}
            sx={{
              color: (_theme) => _theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {mode === 'edit' ? (
            // Edit mode content
            <Grid
              container
              spacing={2}
              size={12}
              sx={{
                mb: 2,
              }}
            >
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'end',
                    width: '40%',
                    justifyContent: 'flex-end',
                  }}
                >
                  <SectionAttributePopover
                    options={filteredAttributeOptions}
                    value={attributeToBeAdded || null}
                    onChange={handleFieldChangeToAddAttribute}
                    label="Add attribute"
                    placeholder="Select attribute"
                    enableSectionFilter={true}
                  />
                  <Button
                    title={
                      !attributeToBeAdded
                        ? 'Select an attribute to add'
                        : `Add ${attributeToBeAdded?.label || ''} attribute to the list`
                    }
                    onClick={handleAddAttribute}
                    variant="contained"
                    disabled={!attributeToBeAdded}
                  >
                    Add
                  </Button>
                </Box>
              </Grid>
              <Grid size={12}>
                {isTableView && (
                  <Grid container rowSpacing={1} size={12}>
                    <Grid size={12}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          '& .MuiTablePagination-root .MuiTablePagination-toolbar':
                            {
                              background: 'transparent !important',
                            },
                        }}
                      >
                        <ReactMuiTableListView
                          data={attributesWithValues}
                          columns={columns}
                          skipPageReset={skipPageReset}
                          getHeaderProps={handleGetHeaderProps}
                          enableRowSelection={true}
                          pageCount={attributesWithValues?.length}
                          enablePagination={true}
                          isAllOptionDisabled={true}
                          initialPageSize={25}
                          rowsPerPageOptions={[25, 50, 100]}
                          stickyHeader={true}
                          dataLength={attributesWithValues?.length || 0}
                          getCellProps={handleGetCellProps}
                        />
                      </Card>
                    </Grid>
                    <ContextMenus
                      menuId="attribute-table-menus"
                      menus={menus}
                      onMenuExit={handleOnMenuExit}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          ) : (
            // View mode content (no stepper)
            <Grid
              container
              spacing={2}
              size={12}
              sx={{
                mb: 2,
              }}
            >
              <Grid size={12}>
                {isTableView && (
                  <Grid container rowSpacing={1} size={12}>
                    <Grid size={12}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          '& .MuiTablePagination-root .MuiTablePagination-toolbar':
                            {
                              background: 'transparent !important',
                            },
                        }}
                      >
                        <ReactMuiTableListView
                          data={attributesWithValues}
                          columns={columns}
                          skipPageReset={skipPageReset}
                          getHeaderProps={handleGetHeaderProps}
                          enableRowSelection={true}
                          pageCount={attributesWithValues?.length}
                          enablePagination={true}
                          isAllOptionDisabled={true}
                          initialPageSize={25}
                          rowsPerPageOptions={[25, 50, 100]}
                          stickyHeader={true}
                          dataLength={attributesWithValues?.length || 0}
                          getCellProps={handleGetCellProps}
                        />
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={2}>
            {mode === 'edit' ? (
              // Edit mode buttons
              <>
                <Button
                  onClick={handleCloseActionModal}
                  variant="outlined"
                  title="Cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveForm}
                  variant="contained"
                  title="Submit"
                >
                  Submit
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleImpact}
                  variant="outlined"
                  color="primary"
                  title="Impact"
                  disabled={
                    impactLoading ||
                    Object.keys(value?.previousValue || {}).length === 0
                  }
                >
                  Impact
                </Button>

                <Button
                  onClick={handleCloseActionModal}
                  variant="outlined"
                  title="Close"
                >
                  Close
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
      {isImpactModalOpen && (
        <ImpactAnalysisModal
          open={isImpactModalOpen}
          onClose={handleCloseImpactModal}
          impactedData={impactedData}
        />
      )}
    </Box>
  );
};

FieldChangeOtherDetails.propTypes = {
  label: PropTypes.node,
  attribute: PropTypes.oneOf([PropTypes.object]).isRequired,
  entityDetailsFormikForm: PropTypes.oneOf([PropTypes.object]).isRequired,
  value: PropTypes.oneOf([PropTypes.object]).isRequired,
  usersList: PropTypes.arrayOf([PropTypes.object]).isRequired,
  groupList: PropTypes.arrayOf([PropTypes.object]),
  teamUsersList: PropTypes.arrayOf([PropTypes.object]),
  mode: PropTypes.oneOf(['edit', 'view']),
  onChange: PropTypes.func.isRequired,
  showLabel: PropTypes.bool,
  modelTemplateDetails: PropTypes.oneOf([PropTypes.object]),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default FieldChangeOtherDetails;
